import React, { useEffect, useState } from 'react';
import { API_CONFIG } from '../../config/api';
import {
  HotTopicCreateRequest,
  TopicDTOAdmin,
  TopicListResponse,
  TopicType,
  TopicUpdateRequestAdmin,
} from '../../types/topic';
import { getHeaders } from '../../utils/api';
import './TopicManagement.css';

const DEFAULT_PAGE_SIZE = 24;

const normalizeTopicResponse = (
  data: unknown,
  requestedPage: number,
  pageSize: number
): TopicListResponse => {
  if (Array.isArray(data)) {
    const topics = data as TopicDTOAdmin[];
    const totalItems = topics.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(requestedPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;

    return {
      topics: topics.slice(startIndex, startIndex + pageSize),
      currentPage: safePage,
      totalPages,
      totalItems,
      pageSize,
    };
  }

  if (data && typeof data === 'object') {
    const response = data as Partial<TopicListResponse> & {
      data?: TopicDTOAdmin[];
    };
    const topics = Array.isArray(response.topics)
      ? response.topics
      : Array.isArray(response.data)
        ? response.data
        : null;

    if (topics) {
      const totalItems = Number(response.totalItems ?? topics.length);
      const totalPages = Math.max(
        1,
        Number(response.totalPages ?? Math.ceil(totalItems / pageSize))
      );

      return {
        topics,
        currentPage: Number(response.currentPage ?? requestedPage),
        totalPages,
        totalItems,
        pageSize: Number(response.pageSize ?? pageSize),
      };
    }
  }

  throw new Error('Invalid response format from server');
};

const TopicManagement = () => {
  const [topics, setTopics] = useState<TopicDTOAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTopic, setEditingTopic] = useState<TopicDTOAdmin | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyNoImage, setShowOnlyNoImage] = useState(false);
  const [selectedType, setSelectedType] = useState<TopicType | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [formData, setFormData] = useState<TopicUpdateRequestAdmin>({
    id: 0,
    name: '',
    logoUrl: ''
  });
  const [hotTopicLoading, setHotTopicLoading] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    topicName: string;
    isGlobal: boolean;
    topicId: number;
  } | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(1);
      setSearchQuery(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();

    const loadTopics = async () => {
      const shouldShowInitialLoader = !hasLoadedOnce;
      if (!shouldShowInitialLoader) {
        setIsFetching(true);
      }

      try {
        setError(null);

        const params = new URLSearchParams({
          page: currentPage.toString(),
          pageSize: DEFAULT_PAGE_SIZE.toString(),
        });

        if (searchQuery) {
          params.append('query', searchQuery);
        }

        if (selectedType !== 'all') {
          params.append('type', selectedType);
        }

        if (showOnlyNoImage) {
          params.append('withoutImage', 'true');
        }

        const response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TOPICS.BASE}?${params.toString()}`,
          {
            headers: getHeaders(),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch topics');
        }

        const data = await response.json();
        const normalized = normalizeTopicResponse(data, currentPage, DEFAULT_PAGE_SIZE);

        setTopics(normalized.topics);
        setCurrentPage(normalized.currentPage);
        setTotalPages(normalized.totalPages);
        setTotalItems(normalized.totalItems);
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }

        setError('Failed to load topics');
        console.error('Error:', err);
      } finally {
        setLoading(false);
        setIsFetching(false);
        setHasLoadedOnce(true);
      }
    };

    void loadTopics();

    return () => controller.abort();
  }, [currentPage, searchQuery, selectedType, showOnlyNoImage, refreshKey, hasLoadedOnce]);

  const getCountryCodeFromStorage = (): string | null => {
    try {
      // First try to get from selectedCountry (used by other components)
      const selectedCountry = localStorage.getItem('selectedCountry');
      if (selectedCountry) {
        const country = JSON.parse(selectedCountry);
        if (country && country.code) {
          return country.code;
        }
      }
    } catch (error) {
      console.error('Error parsing country data from localStorage:', error);
    }
    return null;
  };

  const addToHotTopics = async (topicName: string, isGlobal: boolean) => {
    const selectedTopic = topics.find(t => t.name === topicName);
    if (!selectedTopic) {
      setError('Topic not found');
      return;
    }

    setHotTopicLoading(selectedTopic.id);

    try {
      const countryCode = isGlobal ? undefined : getCountryCodeFromStorage();

      // Debug logging
      console.log('Adding to hot topics:', {
        topicName,
        isGlobal,
        countryCode,
        selectedCountry: localStorage.getItem('selectedCountry'),
        userData: localStorage.getItem('user')
      });

      if (!isGlobal && !countryCode) {
        setError('Country code not found. Please ensure a country is selected in the system.');
        setHotTopicLoading(null);
        return;
      }

      const hotTopicData: HotTopicCreateRequest = {
        topic: topicName,
        iconUrl: selectedTopic.logoUrl || '',
        searchQuery: topicName,
        countryCode: countryCode || undefined,
        isGlobal: isGlobal,
        isActive: false,
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HOT_TOPICS.CREATE}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(hotTopicData),
      });

      if (!response.ok) {
        throw new Error(`Failed to add topic to ${isGlobal ? 'global' : 'country'} hot topics`);
      }

      // Show success message
      alert(`Successfully added "${topicName}" to ${isGlobal ? 'global' : 'country'} hot topics!`);
    } catch (err) {
      setError(`Failed to add topic to ${isGlobal ? 'global' : 'country'} hot topics`);
      console.error('Error:', err);
    } finally {
      setHotTopicLoading(null);
    }
  };

  const handleEdit = (topic: TopicDTOAdmin) => {
    setEditingTopic(topic);
    setFormData({
      id: topic.id,
      name: topic.name,
      logoUrl: topic.logoUrl || ''
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleImageFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyNoImage(e.target.checked);
    setCurrentPage(1);
  };

  const handleTypeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedType(e.target.value as TopicType | 'all');
    setCurrentPage(1);
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TOPICS.UPDATE}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to update topic');
      }

      setRefreshKey(prev => prev + 1);
      setEditingTopic(null);
    } catch (err) {
      setError('Failed to update topic');
      console.error('Error:', err);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    await addToHotTopics(confirmAction.topicName, confirmAction.isGlobal);
    setConfirmAction(null);
  };

  const handleCancelAction = () => {
    setConfirmAction(null);
  };

  if (loading) {
    return <div className="loading">Loading topics...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="topic-management">
      <div className="topic-header">
        <div className="topic-header-row">
          <h2>Topic Management ({totalItems})</h2>
          {isFetching && <span className="topics-status">Refreshing...</span>}
        </div>
        <div className="topic-filters">
          <div className="filter-option">
            <label>
              <input
                type="checkbox"
                checked={showOnlyNoImage}
                onChange={handleImageFilterChange}
              />
              Show only topics without images
            </label>
          </div>
          <div className="type-select">
            <select
              value={selectedType}
              onChange={handleTypeSelect}
              className="type-dropdown"
            >
              <option value="all">All Types</option>
              {Object.values(TopicType).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="search-box">
          <input
            type="text"
            placeholder="Search topics..."
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="topic-results-bar">
        <span>
          Page {totalPages === 0 ? 0 : currentPage} of {totalPages}
        </span>
        <span>{topics.length} topics on this page</span>
      </div>

      <div className="topics-grid">
        {topics.map(topic => (
          <div key={topic.id} className="topic-card">
            {topic.logoUrl ? (
              <img src={topic.logoUrl} alt={topic.name} className="topic-logo" />
            ) : (
              <div className="no-image-placeholder">
                No Image
              </div>
            )}
            <div className="topic-info">
              <h3>{topic.name}</h3>
              <div className="topic-type">
                <span className={`type-badge ${topic.type.toLowerCase()}`}>
                  {topic.type}
                </span>
                {topic.countryCode && (
                  <span className="country-badge">
                    {topic.countryCode}
                  </span>
                )}
              </div>
            </div>
            <div className="topic-actions">
              <button
                className="edit-button"
                onClick={() => handleEdit(topic)}
              >
                Edit
              </button>
              <div className="hot-topic-buttons">
                <button
                  className="hot-topic-btn global-btn"
                  onClick={() => setConfirmAction({
                    topicName: topic.name,
                    isGlobal: true,
                    topicId: topic.id
                  })}
                  disabled={hotTopicLoading === topic.id}
                >
                  {hotTopicLoading === topic.id ? 'Adding...' : 'Add to Global Hot Topic'}
                </button>
                <button
                  className="hot-topic-btn country-btn"
                  onClick={() => setConfirmAction({
                    topicName: topic.name,
                    isGlobal: false,
                    topicId: topic.id
                  })}
                  disabled={hotTopicLoading === topic.id}
                >
                  {hotTopicLoading === topic.id ? 'Adding...' : 'Add to Country Hot Topic'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {topics.length === 0 && !loading && (
        <div className="no-topics">No topics found for the current filters.</div>
      )}

      <div className="pagination">
        <button
          type="button"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage <= 1 || isFetching}
        >
          Previous
        </button>
        <span>
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage >= totalPages || isFetching}
        >
          Next
        </button>
      </div>

      {editingTopic && (
        <div className="edit-modal">
          <div className="modal-content">
            <h3>Edit Topic</h3>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="logoUrl">Logo URL</label>
              <input
                type="text"
                id="logoUrl"
                name="logoUrl"
                value={formData.logoUrl}
                onChange={handleInputChange}
              />
            </div>
            <div className="modal-actions">
              <button onClick={handleSave}>Save</button>
              <button onClick={() => setEditingTopic(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="confirm-modal">
          <div className="modal-content">
            <h3>Confirm Add to Hot Topics</h3>
            <p>
              Are you sure you want to add "{confirmAction.topicName}" to{' '}
              {confirmAction.isGlobal ? 'global' : 'country'} hot topics?
            </p>
            <div className="modal-actions">
              <button
                className="confirm-button"
                onClick={handleConfirmAction}
                disabled={hotTopicLoading === confirmAction.topicId}
              >
                {hotTopicLoading === confirmAction.topicId ? 'Adding...' : 'Confirm'}
              </button>
              <button
                className="cancel-button"
                onClick={handleCancelAction}
                disabled={hotTopicLoading === confirmAction.topicId}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicManagement;
